import { useState } from 'react';
import { useAssignableMembers } from '@/hooks/use-boards';
import { useAuthStore } from '@/stores/auth';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, User, UserCheck } from 'lucide-react';
import type { ProjectMemberItem } from '@hygge/shared';

export function MemberPicker({
  boardId,
  value,
  onChange,
  allowMe = true,
  ariaInvalid,
  ariaLabel = 'Member',
}: {
  boardId: string;
  value: string | undefined;
  onChange: (employeeId: string) => void;
  allowMe?: boolean;
  ariaInvalid?: boolean;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const members = useAssignableMembers(boardId) ?? [];
  const formatName = useEmployeeNameFormatter();
  // Mirror task-config.tsx AssigneeChip: s.user?.employee?.id
  const myEmployeeId = useAuthStore((s) => s.user?.employee?.id);
  const selected = members.find((m: ProjectMemberItem) => m.employeeId === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          aria-label={ariaLabel}
          className="h-9 w-full justify-between font-normal cursor-pointer"
        >
          <span
            className={cn(
              'inline-flex items-center gap-1.5 truncate',
              !selected && 'text-muted-foreground'
            )}
          >
            {selected ? (
              <>
                <Avatar className="h-4 w-4">
                  <AvatarImage src={selected.avatarUrl || undefined} />
                  <AvatarFallback className="text-[6px]">
                    {selected.firstName[0]}
                    {selected.lastName[0]}
                  </AvatarFallback>
                </Avatar>
                {formatName({ firstName: selected.firstName, lastName: selected.lastName })}
              </>
            ) : (
              <>
                <User className="h-3.5 w-3.5" />
                Pick a person…
              </>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search members…" className="h-9" />
          <CommandList>
            <CommandEmpty className="py-4 text-sm text-muted-foreground text-center">
              No members found
            </CommandEmpty>
            <CommandGroup>
              {allowMe && myEmployeeId && value !== myEmployeeId && (
                <CommandItem
                  value="__me__"
                  onSelect={() => {
                    onChange(myEmployeeId);
                    setOpen(false);
                  }}
                  className="cursor-pointer text-primary"
                >
                  <UserCheck className="mr-2 h-4 w-4" />
                  Me
                </CommandItem>
              )}
              {members.map((m: ProjectMemberItem) => (
                <CommandItem
                  key={m.employeeId}
                  // cmdk identifies items by `value` — a bare display name collides
                  // for two members with the same name (both rows would highlight /
                  // select together). Suffix the employee id to keep values unique
                  // while the name part keeps type-ahead search working.
                  value={`${formatName({ firstName: m.firstName, lastName: m.lastName })} ${m.employeeId}`}
                  onSelect={() => {
                    onChange(m.employeeId);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <Avatar className="mr-2 h-5 w-5">
                    <AvatarImage src={m.avatarUrl || undefined} />
                    <AvatarFallback className="text-[7px]">
                      {m.firstName[0]}
                      {m.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">
                    {formatName({ firstName: m.firstName, lastName: m.lastName })}
                  </span>
                  <Check
                    className={cn(
                      'ml-auto h-4 w-4',
                      value === m.employeeId ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function MemberChip({ boardId, value }: { boardId: string; value: string | undefined }) {
  const members = useAssignableMembers(boardId) ?? [];
  const formatName = useEmployeeNameFormatter();
  const m = members.find((x: ProjectMemberItem) => x.employeeId === value);
  if (!m) return <span>{value ?? '—'}</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Avatar className="h-4 w-4">
        <AvatarImage src={m.avatarUrl || undefined} />
        <AvatarFallback className="text-[6px]">
          {m.firstName[0]}
          {m.lastName[0]}
        </AvatarFallback>
      </Avatar>
      {formatName({ firstName: m.firstName, lastName: m.lastName })}
    </span>
  );
}
